import { create } from "zustand";
import { rangeDistanceKm, resolveOdoKm as resolveOdoKmShared } from "../../pages/reports/utils/intervalDistance";
import {
  buildWindows,
  loadDeviceHistory,
  processInBatches,
  DEVICE_CONCURRENCY,
} from "../../pages/reports/utils/trackingHistoryWindows";

const DAY_MS = 24 * 60 * 60 * 1000;

type OdoValue = number | string | null | undefined;

interface RawTrackingPoint {
  ts: number;
  params?: {
    odo_km?: OdoValue;
    odometer_km?: OdoValue;
    virtual_odometer?: OdoValue;
    total_odometer?: OdoValue;
    odometer?: OdoValue;
  } | null;
}

interface OdoPoint {
  ts: number;
  odoKm: number;
}

export interface MileageDevice {
  deviceId: string;
  vehicleName: string;
}

export interface MileageResult {
  deviceId: string;
  vehicleName: string;
  kmInicial: number | null;
  kmFinal: number | null;
  kmRecorrido: number | null;
  firstTs: number | null;
  lastTs: number | null;
  points: number;
  loading: boolean;
  error: string | null;
}

interface ReportMileageState {
  results: Record<string, MileageResult>;
  order: string[];
  loading: boolean;
  from: string | null;
  to: string | null;
  tz: string | null;
  diffDays: number;
}

interface ReportMileageActions {
  loadRange: (
    devices: MileageDevice[],
    from: string,
    to: string,
    opts?: { tz?: string },
  ) => Promise<void>;
  clear: () => void;
}

type ReportMileageStore = ReportMileageState & ReportMileageActions;

const toNumber = (value: OdoValue): number | null => {
  if (value == null || value === "") return null;
  const num = typeof value === "string" ? parseFloat(value) : value;
  return Number.isFinite(num) ? num : null;
};

const resolveOdoKm = (params: RawTrackingPoint["params"]): number | null => {
  if (!params) return null;
  const km = toNumber(params.odo_km) ?? toNumber(params.odometer_km);
  if (km != null) return km;
  const meters =
    toNumber(params.virtual_odometer) ??
    toNumber(params.total_odometer) ??
    toNumber(params.odometer);
  return meters == null ? null : meters / 1000;
};

export const useReportMileageStore = create<ReportMileageStore>()((set) => ({
  results: {},
  order: [],
  loading: false,
  from: null,
  to: null,
  tz: null,
  diffDays: 0,

  loadRange: async (devices, from, to, opts) => {
    const resolvedTz = opts?.tz || Intl.DateTimeFormat().resolvedOptions().timeZone;
    const order = devices.map((d) => d.deviceId);

    const fromMs = new Date(from).getTime();
    const toMs = new Date(to).getTime();
    const windows = buildWindows(fromMs, toMs);
    const diffDays = Math.ceil(Math.abs(toMs - fromMs) / DAY_MS) || 1;

    const initial: Record<string, MileageResult> = {};
    devices.forEach((d) => {
      initial[d.deviceId] = {
        deviceId: d.deviceId,
        vehicleName: d.vehicleName,
        kmInicial: null,
        kmFinal: null,
        kmRecorrido: null,
        firstTs: null,
        lastTs: null,
        points: 0,
        loading: true,
        error: null,
      };
    });

    set({ results: initial, order, loading: true, from, to, tz: resolvedTz, diffDays });

    await processInBatches(devices, DEVICE_CONCURRENCY, async (d) => {
      const { points: rawPoints, failedChunks } = await loadDeviceHistory(
        d.deviceId,
        windows,
        resolvedTz,
      );

      const points: OdoPoint[] = rawPoints
        .map((p) => ({ ts: p.ts, odoKm: resolveOdoKm(p.params as RawTrackingPoint["params"]) }))
        .filter((p): p is OdoPoint => p.odoKm != null && p.ts != null)
        .sort((a, b) => a.ts - b.ts);

      // Lo recorrido sale del odómetro limpio (reseteos y basura fuera) y del GPS
      // donde no hay odómetro, igual que el archivo; restar lecturas pasaba un
      // reseteo entero (GL 31, 15-oct: 944 km contra 857 reales).
      const recorrido = (() => {
        const pts = [...rawPoints]
          .filter((p) => p.ts != null)
          .sort((a, b) => a.ts - b.ts)
          .map((p) => ({ ts: p.ts, lat: p.lat ?? null, lon: p.lng ?? null, speed: p.speed ?? null, odoKm: resolveOdoKmShared(p.params as Record<string, unknown>) }));
        return pts.length ? rangeDistanceKm(pts, pts[0].ts) : null;
      })();

      set((state) => {
        const current = state.results[d.deviceId];
        if (!current) return state;

        const first = points[0] ?? null;
        const last = points[points.length - 1] ?? null;

        return {
          results: {
            ...state.results,
            [d.deviceId]: {
              ...current,
              kmInicial: first ? first.odoKm : null,
              kmFinal: last ? last.odoKm : null,
              // Sin odómetro también: el archivo suma el GPS de esa unidad.
              kmRecorrido: recorrido,
              firstTs: first ? first.ts : null,
              lastTs: last ? last.ts : null,
              points: points.length,
              loading: false,
              error:
                points.length === 0 && failedChunks > 0
                  ? "No se pudo obtener el kilometraje (servicio no disponible)."
                  : points.length === 0
                    ? "Sin datos de odómetro en el rango."
                    : failedChunks > 0
                      ? `${failedChunks} tramo(s) con error`
                      : null,
            },
          },
        };
      });
    });

    set({ loading: false });
  },

  clear: () =>
    set({
      results: {},
      order: [],
      loading: false,
      from: null,
      to: null,
      tz: null,
      diffDays: 0,
    }),
}));
